import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Head from 'next/head';

import Markdown from '@components/Shared/Markdown/Markdown';
import MarkdownStyles from '@components/Shared/Markdown/Markdown.styled';

import { getFullPressRelease } from '@services/socialResponsibility';
import { formatDate, formatCategories } from '@utils/formatters';
import PressReleaseArticleStyles from './PressReleaseArticle.styled';
import Image from 'next/image';
import { formatHttpsUrl } from '@utils/utils';

// This embedded content may not be the same as the one used in Newsroom
import EmbeddedContent from '@components/SocialResponsibility/ReuseContentModule/ContentModules/EmbeddedContent';

const PressReleaseArticle = ({ ssrFullArticle }) => {
  const [fullArticle] = useState(ssrFullArticle);

  return (
    fullArticle && (
      <PressReleaseArticleStyles>
        <div className="constrained-width">
          <Head>
            <title>{fullArticle.titleTag}</title>
            <meta name="description" content={fullArticle.metaDescription} />
            <meta
              property="og:image"
              content={fullArticle.featuredMedia.media.url}
            />
          </Head>
          <div className="articleContainer">
            <p className="pr-categories">
              {fullArticle.categories
                ? formatCategories(fullArticle.categories)
                : ''}
            </p>
            <h3>{fullArticle.headline}</h3>
            <p className="subheadline">{fullArticle.subHeadline}</p>
            <h6 className="publication-date">
              {formatDate(fullArticle.publicationDate)}
            </h6>

            {fullArticle.featuredMedia ? (
              <div className="featured-image">
                <Image
                  src={formatHttpsUrl(
                    `${fullArticle.featuredMedia.media.url}?w=808&fm=webp`
                  )}
                  alt={fullArticle.featuredMedia.altText}
                  width={808}
                  height={540}
                />
              </div>
            ) : null}

            {/* See note at top. I wrapped each Markdown with MarkdownStyles to get the styling that
                        already existed in Newsroom PR article. The newsroom Markdown component included the
                        MarkdownStyles internally, but I did not do that here because this project has a BodyContent
                        component that uses Markdown component also. If BodyContent goes away, we can put MarkdownStyles
                        inside Markdown I we choose.
                    */}
            <MarkdownStyles>
              <Markdown markdown={fullArticle && fullArticle.articleContent1} />
            </MarkdownStyles>

            {/** reusable content modules go below content 1, unless there is no content 1 **/}
            {fullArticle.reuseContentModules
              ? fullArticle.reuseContentModules.map((module, i) => (
                  <EmbeddedContent
                    key={`emb-content-${i}`}
                    embeddedContentObj={module}
                  />
                ))
              : null}

            {fullArticle && fullArticle.articleContent2 && (
              <MarkdownStyles>
                <Markdown markdown={fullArticle.articleContent2} />
              </MarkdownStyles>
            )}

            {fullArticle.boilerPlate &&
              fullArticle.boilerPlate.map((text, i) => (
                <MarkdownStyles key={`boilerplate-${i}`}>
                  <Markdown markdown={text.bodyText} />
                </MarkdownStyles>
              ))}
          </div>
        </div>
      </PressReleaseArticleStyles>
    )
  );
};

export async function getServerSideProps({ params }) {
  let fullArticle = null;

  try {
    await getFullPressRelease(params.slug).then((result) => {
      fullArticle = result;
    });

    if (!fullArticle) {
      return {
        props: {
          notFound: true,
        },
      };
    }
  } catch (error) {
    console.error(error);
  }

  return {
    props: {
      ssrFullArticle: fullArticle,
    },
  };
}

export default PressReleaseArticle;

PressReleaseArticle.propTypes = {
  ssrFullArticle: PropTypes.object,
  match: PropTypes.object,
};
